import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar } from '@mantine/core';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CampaignClass } from '@feathr/blackbox';
import { Button, Heading, Pill, PixelWellWithHighlight, Text, Tooltip } from '@feathr/components';
import { CampaignIcon } from '@feathr/extender/components/CampaignIcons';
import { getIconForAction } from '@feathr/hooks';

import * as styles from './PixlPlusCard.css';

const LEARN_MORE_URL =
  'https://help.feathr.co/hc/en-us/articles/34132908338711-What-is-a-Pixl-Plus-Ad-Campaign';

export interface IPixlPlusCardProps {
  onClick: (type: CampaignClass) => void;
}

function PixlPlusCard({ onClick }: Readonly<IPixlPlusCardProps>): JSX.Element {
  const { t } = useTranslation();

  const channelAccents: ReactNode = (
    <Avatar.Group spacing={'6'}>
      <Tooltip title={t('Meta')}>
        <Avatar alt={t('Meta')} color={'gray.1'} size={'20'} variant={'filled'}>
          <span className={styles.metaIcon}>{getIconForAction('meta')}</span>
        </Avatar>
      </Tooltip>
      <Tooltip title={t('Display Advertising')}>
        <Avatar alt={t('Display Advertising')} color={'cyan.6'} size={'20'} variant={'filled'}>
          {getIconForAction('displayAd')}
        </Avatar>
      </Tooltip>
    </Avatar.Group>
  );

  const lookalikeAccent: ReactNode = (
    <Avatar.Group spacing={'6'}>
      <Tooltip title={t('Lookalike')}>
        <Avatar alt={t('Lookalike')} color={'yellow.6'} size={'20'} variant={'filled'}>
          {getIconForAction('lookalike')}
        </Avatar>
      </Tooltip>
    </Avatar.Group>
  );

  const features: Array<{ label: string; text: string; accent?: ReactNode }> = [
    {
      label: t('Channels'),
      text: t('Reach supporters across Display and Meta'),
      accent: channelAccents,
    },
    {
      label: t('Data sources'),
      text: t('First-party audiences and third-party targeting'),
    },
    {
      label: t('Lookalike targeting'),
      text: t('Expand reach to supporters similar to your audience.'),
      accent: lookalikeAccent,
    },
    {
      label: t('Campaign objectives'),
      text: t('Awareness, engagement, or conversions'),
    },
  ];

  function handleGetStarted(): void {
    onClick(CampaignClass.Omni);
  }

  return (
    <PixelWellWithHighlight
      className={styles.root}
      contentClassName={styles.content}
      data-appcues={CampaignClass.Omni}
      data-name={'pixl-plus-card'}
      highlighted={true}
      name={'Pixl Plus'}
    >
      <CampaignIcon className={styles.icon} cls={CampaignClass.Omni} hover={false} size={48} />
      <div className={styles.body}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Heading className={styles.title} order={4} variant={'h4'}>
              {t('Pixl Plus')}
            </Heading>
            <Pill className={styles.badge} theme={'violet'} truncate={false}>
              {t('Recommended for multi-channel campaigns')}
            </Pill>
          </div>
          <div className={styles.actions}>
            <Button name={'pixl-plus-get-started'} onClick={handleGetStarted} type={'primary'}>
              {t('Get started')}
            </Button>
            <Button
              href={LEARN_MORE_URL}
              name={'pixl-plus-learn-more'}
              target={'_blank'}
              type={'secondary'}
            >
              {t('Learn more')}
            </Button>
          </div>
        </div>
        <Text className={styles.description} truncate={false} variant={'defaultRegular'}>
          {t(
            'Our all-in-one advertising campaign. Reach supporters across channels with first-party and third-party data, fine-tune targeting, and drive the outcomes that matter most.',
          )}
        </Text>
        <div className={styles.featureList}>
          {features.map((feature) => (
            <div className={styles.feature} key={feature.label}>
              <span className={styles.featureCheck}>
                <FontAwesomeIcon icon={faCheck} />
              </span>
              <div className={styles.featureBody}>
                <Heading className={styles.featureLabel} order={5} variant={'h5'}>
                  {feature.label}
                  {feature.accent}
                </Heading>
                <Text className={styles.featureText} truncate={false} variant={'defaultRegular'}>
                  {feature.text}
                </Text>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PixelWellWithHighlight>
  );
}

export default PixlPlusCard;
